import { NavigableModal } from '$components'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { EditVeterinaryInformationForm, EditVeterinaryInformationSchema, Input } from '$features/pets/forms/edit-veterinary-information/index'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { petShowOptions, petShowQueryKey, petUpdateMutation } from '$api-client/@tanstack/react-query.gen'
import { ToastHelper } from '$components'
import { PetsFilterParam } from '$features/pets/enums/filter-params'

export function EditVeterinaryInformationModal() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const { data } = useQuery({
    ...petShowOptions({
      path: {
        pet: Number(id),
      },
      query: {
        include_deleted: true,
        [PetsFilterParam.IncludeUnapproved]: searchParams.get(PetsFilterParam.IncludeUnapproved) === '1',
      },
    }),
  })

  const { mutate: updatePet, isPending: isUpdatingPet } = useMutation({
    ...petUpdateMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: petShowQueryKey({
          path: {
            pet: Number(id),
          },
        }),
      })
      ToastHelper.success('Pet veterinary information updated successfully.')
      handleClose()
    },
    onError: () => ToastHelper.error('Failed to update pet veterinary information.'),
  })

  const handleClose = () => navigate(-1)

  const handleSubmit = async (values: EditVeterinaryInformationSchema) => {
    updatePet({
      path: {
        pet: Number(id),
      },
      body: {
        vet_name: values[Input.VetName],
        vet_phone: values[Input.VetPhone],
      },
    })
  }

  const pet = data?.data

  if (!pet) {
    return null
  }

  return (
    <EditVeterinaryInformationForm
      onSubmit={handleSubmit}
      onCancel={handleClose}
      isLoading={isUpdatingPet}
      defaultValues={{
        [Input.VetName]: pet.vet_name ?? undefined,
        [Input.VetPhone]: pet.vet_phone ?? undefined,
      }}
    >
      <NavigableModal onClose={handleClose}>
        <NavigableModal.Header>
          <NavigableModal.Heading>
            Edit Veterinary Information
          </NavigableModal.Heading>
        </NavigableModal.Header>

        <NavigableModal.Content>
          <EditVeterinaryInformationForm.Content />
        </NavigableModal.Content>

        <NavigableModal.Footer>
          <EditVeterinaryInformationForm.Controls />
        </NavigableModal.Footer>
      </NavigableModal>
    </EditVeterinaryInformationForm>
  )
}
